import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import {isEqual} from 'lodash';
import PropTypes from 'prop-types';
import Honeybadger from '@honeybadger-io/js';
import RegistryItemGatewayApi from '../../services/RegistryItemGatewayApi';
import {FEATURED, getSortOptionDetails} from '../../constants/sortOptions';
import {
  CASH_FUND_TYPE,
  TRANSACTIONAL_PRODUCT_TYPE,
  UNIVERSAL_REGISTRY_PRODUCT_TYPE,
  RETAILER_PRODUCT_TYPE,
} from '../../constants';
import MemberContext from '../MemberContext';
import useItemFilters from './hooks/useItemFilters';
import useShouldFetchItems from './hooks/useShouldFetchItems';
import useOOSTopSort from '../../hooks/useOOSTopSort';
import noop from '../../utils/noop';

const OR_SEPERATOR = '|';

const initialState = {
  canUndoFilters: null,
  cashFundCount: 0,
  fetchItems: noop,
  refreshItems: noop,
  filters: {
    price: '',
    status: '',
    type: '',
    stores: '',
  },
  hasError: false,
  hasItems: false,
  isFetching: true,
  itemCount: 0,
  items: [],
  registryItemsUpdatedInSessionRef: [],
  itemStores: [],
  page: 1,
  resetFilters: noop,
  setCashFundCount: noop,
  setFilters: noop,
  setItems: noop,
  setItemCount: noop,
  setPage: noop,
  setSort: noop,
  sort: FEATURED,
  totalPageCount: 0,
  undoFilters: noop,
};

const Context = createContext(initialState);

export default Context;

export const ItemsContextProvider = ({children}) => {
  const {memberId} = useContext(MemberContext);
  const [totalPageCount, setTotalPageCount] = useState(0);
  const [isFetchingItems, setIsFetchingItems] = useState(true);
  const [itemCount, setItemCount] = useState(0);
  const [cashFundCount, setCashFundCount] = useState(0);
  const [hasApiError, setHasApiError] = useState(false);
  const [items, setItems] = useState([]);
  const [itemStores, setItemStores] = useState([]);
  const [refreshItemsSignal, setRefreshItemsSignal] = useState(0);
  const {shouldTopSort} = useOOSTopSort();

  /**
   * due to some React state timing issues, we need to use a ref
   * to store the registry items that were updated in the session
   */
  const registryItemsUpdatedInSessionRef = useRef([]);

  const {
    filters,
    previousFilters,
    setFilters,
    undoFilters,
    canUndoFilters,
    resetFilters,
    sort,
    page,
    setSort,
    setPage,
    hasAppliedFilters,
    previousPage,
    previousSort,
  } = useItemFilters();

  const fetchItems = useCallback(
    async oosTopSort => {
      try {
        setIsFetchingItems(true);

        const sortOptionDetails = getSortOptionDetails(sort);
        const offset = (page - 1) * 100;
        const {type, status, price, stores} = filters;
        const priceRange = price.split(OR_SEPERATOR);
        const types = [];

        if (type) {
          if (type.split(OR_SEPERATOR).includes('item')) {
            types.push([
              RETAILER_PRODUCT_TYPE,
              TRANSACTIONAL_PRODUCT_TYPE,
              UNIVERSAL_REGISTRY_PRODUCT_TYPE,
            ]);
          }

          if (type.split(OR_SEPERATOR).includes('cash fund')) {
            types.push(CASH_FUND_TYPE);
          }
        }
        const response = await RegistryItemGatewayApi.getRegistryItems({
          memberId,
          // Always include cash funds for member manage. Gating on
          // hasCashRegistry leaves the grid stale after first-time cash fund
          // creation because CoupleContext is not refreshed yet (CASH-196).
          showCash: true,
          sort: sortOptionDetails && sortOptionDetails.value,
          price: priceRange,
          stores,
          offset,
          status,
          types,
          sortUnavailable:
            shouldTopSort || oosTopSort === true ? 'top' : 'bottom',
        });

        const {data, countsByType, count, stores: newStores} = response;

        setIsFetchingItems(false);
        setItems(data);
        setTotalPageCount(Math.ceil(count / 100));

        if (!isEqual(newStores, itemStores)) {
          setItemStores(newStores);
        }

        try {
          const fundCount = countsByType.CASH_FUND.uniqueCount;

          const physicalItemCount = Object.keys(countsByType).reduce(
            (sum, itemType) => {
              if (itemType !== 'CASH_FUND') {
                return sum + countsByType[itemType].totalRequested;
              }

              return sum;
            },
            0,
          );

          setCashFundCount(fundCount);
          setItemCount(physicalItemCount);
          setHasApiError(false);
        } catch (error) {
          Honeybadger.notify(
            error,
            'FetchRegistryItemsError physicalItemCount',
          );
          throw error;
        }
      } catch (error) {
        setIsFetchingItems(false);
        setHasApiError(true);
        Honeybadger.notify(error, 'FetchRegistryItemsError');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      sort,
      page,
      filters.price,
      filters.type,
      filters.status,
      filters.stores,
      memberId,
      shouldTopSort,
    ],
  );

  const refreshItems = useCallback(() => {
    setRefreshItemsSignal(previous => previous + 1);
  }, []);

  useShouldFetchItems(
    sort,
    page,
    filters,
    previousSort,
    previousPage,
    previousFilters,
    refreshItemsSignal,
    fetchItems,
  );

  return (
    <Context.Provider
      value={{
        items,
        filters,
        setFilters,
        undoFilters,
        canUndoFilters,
        resetFilters,
        sort,
        page,
        fetchItems,
        refreshItems,
        totalPageCount,
        itemCount,
        cashFundCount,
        setItems,
        setCashFundCount,
        setItemCount,
        isFetching: isFetchingItems,
        hasError: hasApiError,
        setSort,
        setPage,
        hasAppliedFilters,
        hasItems: items?.length > 0,
        itemStores,
        registryItemsUpdatedInSessionRef,
      }}>
      {children}
    </Context.Provider>
  );
};

ItemsContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
